import type { MutableRefObject } from 'react';
import { useRef } from 'react';
import { getNextAnimationFrame } from '@elfsight/lib-utils';
import type { CurveOfTransition } from '../types';
import { matchCurve } from './curves';
import { getColumnWidths } from './get-column-widths';

type SlideScrollAnimation = {
  carouselRef: MutableRefObject<HTMLDivElement | null>;
  curveOfSlideTransition: CurveOfTransition;
  durationTransition: number;
  columnsGap: number;
  boundariesPadding: number;
};

export const useSlideScrollAnimation = ({
  carouselRef,
  curveOfSlideTransition,
  columnsGap,
  durationTransition,
  boundariesPadding
}: SlideScrollAnimation) => {
  const animationFrameRef = useRef(0);

  const scrollTo = (columnIndex: number, instantly = false) => {
    const { current } = carouselRef;

    if (!current) {
      return;
    }
    cancelAnimationFrame(animationFrameRef.current);

    const columnWidths = getColumnWidths(carouselRef);

    const offsetX =
      columnWidths.reduce((acc, cur, index) => {
        if (index < columnIndex) {
          return acc + cur + columnsGap;
        }
        return acc;
      }, 0) -
      boundariesPadding / 2;

    const fromPos = current.scrollLeft;
    let toPos = offsetX;

    // `toPos` must be reachable for `scrollLeft` value
    if (current.scrollWidth < offsetX + current.offsetWidth) {
      toPos = current.scrollWidth - current.offsetWidth;
    }

    if (instantly) {
      current.scrollLeft = toPos;
      return;
    }

    const easingFn = matchCurve[curveOfSlideTransition];

    // init animation value
    let animationPosition = fromPos;

    // turn off scrollSnap, because `scrollLeft` does not work due snap
    current.style.scrollSnapType = 'none';

    const animateScroll = () => {
      animationFrameRef.current = requestAnimationFrame(() => {
        if (!carouselRef.current) {
          return;
        }

        const { eased, linear, progress } = getNextAnimationFrame(
          animationPosition,
          fromPos,
          toPos,
          durationTransition,
          easingFn
        );

        carouselRef.current.scrollLeft = eased;
        // store linear value for next animation update
        animationPosition = linear;

        if ((progress || 0) < 1) {
          animateScroll();
        } else {
          // animation done
          carouselRef.current.style.scrollSnapType = 'x mandatory';
        }
      });
    };
    animateScroll();
  };

  return scrollTo;
};
